import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import {
  ArrowRight, BookOpen, Library, Shirt, Footprints, PenTool,
  Trophy, FlaskConical, Monitor, CheckCircle2,
  FileCheck2, ShieldCheck, Layers, ScrollText, Timer, Sparkles,
  Clock, Handshake, Plus, Minus,
} from "lucide-react";

/* ------------------------------------------------------------------ *
 *  Categories — Academic, Library and Stationery are LIVE            *
 *  Every other category shown as Coming Soon                         *
 * ------------------------------------------------------------------ */
const PRIMARY = [
  {
    name: "Academic Books",
    desc: "Prescribed textbooks, workbooks and reference guides — ICSE, ISC, CBSE.",
    icon: BookOpen,
    photo: "/categories/library-academic-books.jpg",
  },
  {
    name: "Library Books",
    desc: "Curated storybooks, encyclopaedias and reading collections for school libraries.",
    icon: Library,
    photo: "/categories/library-academic-books.jpg",
  },
  {
    name: "Stationery",
    desc: "Notebooks, writing supplies and classroom essentials — coordinated for whole-school orders.",
    icon: PenTool,
    photo: "/categories/stationery-office-supplies.jpg",
  },
];

const COMING_SOON = [
  { name: "Uniforms",           icon: Shirt,        photo: "/categories/uniforms-accessories.jpg" },
  { name: "School Shoes",       icon: Footprints,   photo: "/categories/school-shoes.jpg" },
  { name: "Sports Equipment",   icon: Trophy,       photo: "/categories/sports-physical-education.jpg" },
  { name: "Laboratory Supplies",icon: FlaskConical, photo: "/categories/science-lab-supplies.jpg" },
  { name: "IT Equipment",       icon: Monitor,      photo: "/categories/it-smart-classroom.jpg" },
  { name: "Art & Activity",     icon: Sparkles,     photo: "/categories/art-activity-materials.jpg" },
];

/* ------------------------------------------------------------------ *
 *  Trust pillars                                                     *
 * ------------------------------------------------------------------ */
const TRUST = [
  { icon: Layers,       title: "Transparent Procurement Workflow",
    body: "Every requisition, approval and purchase order is visible and traceable end-to-end." },
  { icon: ShieldCheck,  title: "Vendor Verification Process",
    body: "Every supplier goes through structured onboarding, GST and reference verification." },
  { icon: FileCheck2,   title: "Centralised Order Management",
    body: "Track every order from placement to delivery in one place — no chasing spreadsheets." },
  { icon: ScrollText,   title: "Digital Audit Trail",
    body: "Time-stamped audit log for every action — ready for board reviews and audits." },
  { icon: Timer,        title: "Faster Procurement Cycles",
    body: "Cut coordination time from weeks to days with structured workflows and reminders." },
  { icon: Sparkles,     title: "Reduced Administrative Workload",
    body: "Automate paperwork so your team can focus on students, not on chasing vendors." },
];

/* ------------------------------------------------------------------ *
 *  FAQ                                                                *
 * ------------------------------------------------------------------ */
const FAQ_POPULAR = [
  {
    q: "How do schools order through SchoolLane?",
    a: "Schools can submit their procurement requirements through SchoolLane and receive coordinated support for sourcing, ordering and fulfillment. The platform helps schools streamline vendor coordination, order tracking and procurement communication through a single interface.",
  },
  {
    q: "Can parents order online?",
    a: "Yes. SchoolLane is building a convenient digital ordering experience that allows parents to place orders for approved school books and other school-related requirements online, track order status and receive timely updates.",
  },
  {
    q: "Can schools continue working with their existing suppliers?",
    a: "Absolutely. SchoolLane is designed to support schools, not replace their trusted vendor relationships. Schools may continue working with their preferred publishers, distributors and suppliers while using SchoolLane to simplify coordination, order management and communication.",
  },
  {
    q: "Which boards do you support?",
    a: "SchoolLane aims to support schools across multiple educational boards, including ICSE, ISC, CBSE and State Boards. Support may vary based on school requirements, curriculum and supplier availability.",
  },
];

const FAQ_MORE = [
  {
    q: "What products are currently available through SchoolLane?",
    a: "Currently, SchoolLane is focused on Academic Books and Library Books. Additional categories such as uniforms, stationery, laboratory supplies, sports equipment and other school procurement requirements will be introduced in future phases.",
  },
  {
    q: "Does SchoolLane charge schools any subscription fee?",
    a: "SchoolLane works with each institution based on its specific requirements and procurement model. Schools interested in partnering with SchoolLane may contact us for a consultation and customised proposal.",
  },
  {
    q: "How does SchoolLane help schools save time?",
    a: "Schools often spend significant administrative time coordinating with multiple vendors, collecting quotations, tracking deliveries and resolving order-related issues. SchoolLane centralises these activities into a single workflow, helping schools reduce administrative effort and improve procurement visibility.",
  },
  {
    q: "How does SchoolLane help parents?",
    a: "SchoolLane aims to make school-related purchases more convenient by reducing manual processes and providing a more organised ordering experience. Benefits include reduced waiting time, easier ordering, better order visibility, simplified communication and faster issue resolution.",
  },
  {
    q: "Can SchoolLane manage book distribution for schools?",
    a: "Yes. SchoolLane is designed to support schools in organising and managing academic book procurement and distribution processes, helping improve coordination between schools, suppliers and parents.",
  },
  {
    q: "Is SchoolLane only for large schools?",
    a: "No. SchoolLane is designed for schools of different sizes and can be adapted to suit the needs of both small and large educational institutions.",
  },
  {
    q: "How do I request a demo?",
    a: "Schools can request a personalised demonstration by clicking the \"Request Procurement Consultation\" or \"Book a Demo\" button on the website, or by contacting us directly via phone, email or WhatsApp.",
  },
  {
    q: "Is SchoolLane available outside Kolkata?",
    a: "SchoolLane is currently focused on building partnerships with schools in Kolkata and surrounding regions. Expansion into additional locations will be undertaken in phases.",
  },
  {
    q: "How can a school become a founding partner?",
    a: "Schools interested in participating in SchoolLane's Founding School Partner Program can contact us directly. Founding partners will have the opportunity to provide feedback and help shape the future development of the platform.",
  },
  {
    q: "Who should schools contact for partnership enquiries?",
    a: "Schools may contact SchoolLane at +91 9674259387, by email at schoollane.in@gmail.com, or via WhatsApp Chat on the website for procurement consultations, demonstrations and partnership discussions.",
  },
  {
    q: "Why should schools use SchoolLane instead of managing procurement directly?",
    a: "SchoolLane helps schools reduce the administrative burden associated with procurement by bringing communication, vendor coordination, order management and tracking into a single workflow. This enables school staff to spend less time managing procurement activities and more time focusing on academic and administrative priorities.",
  },
];

const ALL_FAQS = [...FAQ_POPULAR, ...FAQ_MORE];

function FaqItem({ q, a, defaultOpen = false, testid }) {
  return (
    <details
      className="group border-b border-slate-200 last:border-b-0 py-2"
      {...(defaultOpen ? { open: true } : {})}
      data-testid={testid}
    >
      <summary
        className="flex items-start justify-between gap-4 py-4 cursor-pointer
                   list-none font-heading font-semibold text-slate-900
                   text-base sm:text-lg hover:text-blue-700 transition-colors"
      >
        <span>{q}</span>
        <span className="shrink-0 mt-1 w-7 h-7 rounded-full bg-slate-100 text-slate-600
                         grid place-items-center group-open:bg-blue-100 group-open:text-blue-700 transition-colors">
          <Plus size={16} className="group-open:hidden" />
          <Minus size={16} className="hidden group-open:block" />
        </span>
      </summary>
      <div className="pb-5 pr-10 text-sm sm:text-base text-slate-600 leading-relaxed">
        {a}
      </div>
    </details>
  );
}

export default function Home() {
  return (
    <div className="bg-white">
      <PublicNav />

      {/* ─── Hero — clean, SaaS-first, sage-toned ─────────────────── */}
      <section className="relative bg-white overflow-hidden">
        {/* Soft sage tint behind the whole hero */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(1200px 500px at 15% 30%, #E7EEDF 0%, rgba(231,238,223,0) 60%), " +
              "radial-gradient(900px 400px at 90% 70%, #DDE7CE 0%, rgba(221,231,206,0) 55%)",
          }}
        />
        {/* Very soft dot texture — barely visible, keeps hero corporate */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(#5B7A4B 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 pt-14 pb-12 lg:pt-20 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-brand"
              className="font-heading font-bold tracking-tight leading-[0.95]
                         text-6xl sm:text-7xl lg:text-[6.5rem]"
            >
              <span className="text-[#003870]">School</span>
              <span className="text-[#D0A008]">Lane</span>
            </h1>
            <div
              data-testid="hero-headline"
              className="mt-4 font-heading font-semibold tracking-tight text-slate-800
                         text-2xl sm:text-3xl lg:text-4xl leading-[1.1]"
            >
              School Procurement Made Simple.
            </div>
            <p
              data-testid="hero-subheadline"
              className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl"
            >
              SchoolLane helps schools manage academic books, library books and
              educational procurement through a single streamlined platform.
              Reduce vendor coordination, eliminate paperwork, save
              administrative time — and give parents a smoother ordering
              experience.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-consult"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md
                           bg-blue-700 text-white font-semibold text-sm
                           hover:bg-blue-800 transition-colors duration-200 shadow-sm"
              >
                Request Procurement Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-demo"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md
                           border border-slate-300 bg-white text-slate-800 font-semibold text-sm
                           hover:border-slate-400 hover:bg-slate-50 transition-colors duration-200"
              >
                Book a Demo
              </Link>
              <Link
                to="/vendors"
                data-testid="hero-cta-partner"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md
                           border border-emerald-200 bg-emerald-50 text-emerald-800 font-semibold text-sm
                           hover:bg-emerald-100 transition-colors duration-200"
              >
                Partner With SchoolLane
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs text-slate-600">
              <span className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-600" /> Single point of contact
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-600" /> Transparent approvals
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-600" /> Complete audit trail
              </span>
            </div>
          </div>

          {/* Real photograph — top-down school supplies (Unsplash, no auth) */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-white/40 backdrop-blur-sm border border-white/60" />
              <div className="relative rounded-3xl overflow-hidden shadow-xl ring-1 ring-black/5">
                <img
                  src="https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?auto=format&fit=crop&w=1200&q=80"
                  alt="Top-down flatlay of a school backpack with books, notebooks, pencils and stationery on a sage background"
                  loading="eager"
                  className="w-full h-[340px] sm:h-[420px] lg:h-[460px] object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(15,23,42,0.28) 100%)",
                  }}
                />
                {/* Compact glass strip: proof of workflow */}
                <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur rounded-xl border border-white/60 px-4 py-3 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-700 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    Order placed · awaiting principal approval
                  </div>
                  <span className="text-slate-500 hidden sm:inline">Class 8 · Library</span>
                </div>
              </div>
              {/* Floating credential chip */}
              <div className="hidden md:flex absolute -top-4 -left-4 items-center gap-2 bg-white rounded-xl border border-slate-200 shadow-lg px-3 py-2 text-[11px] font-semibold text-slate-700">
                <ShieldCheck size={14} className="text-emerald-600" />
                Audit-ready trail
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section divider ───────────────────────────────────────── */}
      <div className="border-t border-slate-100" />

      {/* ─── What SchoolLane covers today ──────────────────────────── */}
      <section id="categories" className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-semibold text-blue-700 tracking-wider uppercase">
            Our current focus
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold text-slate-900">
            Live categories
          </h2>
          <p className="mt-3 text-slate-600">
            We are starting with the two categories that consume most of a school's
            procurement time each academic year. More categories will roll out as
            we scale.
          </p>
        </div>

        {/* Primary (live) — photo + copy */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {PRIMARY.map((c) => (
            <div
              key={c.name}
              data-testid={`cat-live-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-blue-300 hover:shadow-md transition-all duration-300"
            >
              <div className="relative h-40 bg-slate-100 overflow-hidden">
                <img src={c.photo} alt={`${c.name} — SchoolLane`}
                     loading="lazy"
                     className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-white/95 border border-emerald-200 rounded-full px-2 py-0.5 shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 grid place-items-center shrink-0">
                    <c.icon size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-slate-900">
                      {c.name}
                    </h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Coming soon — subdued thumbnails with badge overlay */}
        <div>
          <div className="text-xs font-semibold text-slate-500 tracking-wider uppercase mb-4">
            Coming soon
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {COMING_SOON.map((c) => (
              <div
                key={c.name}
                data-testid={`cat-coming-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="rounded-xl border border-slate-200 bg-white overflow-hidden text-center"
              >
                <div className="relative h-24 bg-slate-100 overflow-hidden">
                  <img src={c.photo} alt=""
                       loading="lazy"
                       className="w-full h-full object-cover opacity-60 grayscale" />
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-700 bg-white/95 border border-slate-200 rounded-full px-2 py-0.5">
                    Soon
                  </span>
                </div>
                <div className="px-3 py-2.5">
                  <div className="w-8 h-8 mx-auto -mt-8 rounded-lg bg-white border border-slate-200 text-slate-500 grid place-items-center shadow-sm relative z-10">
                    <c.icon size={16} />
                  </div>
                  <div className="mt-1.5 text-xs font-medium text-slate-700">{c.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Schools Trust SchoolLane ─────────────────────────── */}
      <section id="trust" className="bg-slate-50 border-y border-slate-100 py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-12 text-center mx-auto">
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">
              Why Schools Trust SchoolLane
            </h2>
            <p className="mt-3 text-slate-600">
              Built around the principles Indian school principals and accountants
              tell us they need most.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TRUST.map((t) => (
              <div
                key={t.title}
                data-testid={`trust-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g,'')}`}
                className="rounded-2xl bg-white border border-slate-200 p-6"
              >
                <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
                  <t.icon size={20} />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-slate-900">
                  {t.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <div className="text-xs font-semibold text-blue-700 tracking-wider uppercase">
            How it works
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold text-slate-900">
            A cleaner path from requisition to delivery
          </h2>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6 counter-reset">
          {[
            { t: "Onboard the school",
              d: "We map your existing procurement flow, approval hierarchy and vendor list into SchoolLane in a short setup call." },
            { t: "Run structured procurement",
              d: "Teachers raise requisitions, department heads, principals and accounts approve — every step time-stamped." },
            { t: "Track & audit",
              d: "Live dashboards, GST-ready invoicing and a full audit log make every purchase reviewable in one click." },
          ].map((s, i) => (
            <li key={s.t} className="relative rounded-2xl border border-slate-200 bg-white p-7">
              <div className="w-9 h-9 rounded-lg bg-blue-700 text-white grid place-items-center font-heading font-bold">
                {i + 1}
              </div>
              <div className="mt-4 font-heading font-semibold text-slate-900">{s.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ─── Frequently Asked Questions ───────────────────────────── */}
      <section id="faq" className="bg-slate-50 border-y border-slate-100 py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6" data-testid="faq-section">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-semibold text-blue-700 tracking-wider uppercase">
              FAQ
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold text-slate-900">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-slate-600">
              The questions school principals, accountants and parents most often
              ask us before getting started.
            </p>
          </div>

          {/* Popular */}
          <div className="rounded-2xl bg-white border border-slate-200 p-2 sm:p-4 mb-8"
               data-testid="faq-popular-group">
            {FAQ_POPULAR.map((f, i) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                defaultOpen={i === 0}
                testid={`faq-item-popular-${i}`}
              />
            ))}
          </div>

          {/* More */}
          <details className="group rounded-2xl bg-white border border-slate-200 p-2 sm:p-4"
                   data-testid="faq-more-group">
            <summary
              className="flex items-center justify-between px-3 py-3 cursor-pointer list-none
                         font-heading font-semibold text-slate-900 hover:text-blue-700 transition-colors"
            >
              <span className="text-base sm:text-lg">More questions</span>
              <span className="inline-flex items-center gap-2 text-xs text-slate-500 font-medium">
                {FAQ_MORE.length} more
                <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-600
                                 grid place-items-center group-open:bg-blue-100 group-open:text-blue-700 transition-colors">
                  <Plus size={16} className="group-open:hidden" />
                  <Minus size={16} className="hidden group-open:block" />
                </span>
              </span>
            </summary>
            <div className="mt-2 border-t border-slate-100">
              {FAQ_MORE.map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} testid={`faq-item-more-${i}`} />
              ))}
            </div>
          </details>

          {/* Still-have-questions callout */}
          <div className="mt-10 rounded-2xl border border-blue-100 bg-blue-50 p-6 sm:p-7
                          flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
               data-testid="faq-cta">
            <div>
              <div className="font-heading font-semibold text-slate-900">
                Still have questions?
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Talk to our team on WhatsApp, phone or email — we usually reply
                within a few hours.
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="faq-cta-consult"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md
                           bg-blue-700 text-white font-semibold text-sm hover:bg-blue-800 transition-colors"
              >
                Request Procurement Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="faq-cta-demo"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md
                           border border-slate-300 bg-white text-slate-800 font-semibold text-sm
                           hover:border-slate-400 hover:bg-slate-50 transition-colors"
              >
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA — plain, professional ──────────────────────── */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl lg:text-3xl font-heading font-semibold">
              Ready to bring order to your procurement?
            </h3>
            <p className="text-slate-300 mt-3 max-w-xl">
              Book a 20-minute walkthrough with our team. No obligation, no sales
              pressure — just a clear picture of whether SchoolLane fits your
              school.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="footer-cta-consult"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 transition-colors duration-200"
            >
              Request Procurement Consultation <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="footer-cta-demo"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors duration-200"
            >
              Book a Demo
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
